import { useTranscriptionMutation } from "@common/api/traces"
import { StartStop } from "@common/components/trace/recorder"
import { LoadingSpinner } from "@common/components/ui/loading-spinner"
import { LinearAudioLevels } from "@common/features/recording/audio-levels"
import { useWhisperTranscription } from "@common/features/recording/hooks/use-whisper-transcription"
import { toast } from "@common/hooks/use-toast"
import { getErrorMessage } from "@common/utils"
import { Check, Mic, X } from "lucide-react"

function keepFocus(event: React.MouseEvent) {
  event.preventDefault()
}

function showFailure(title: string) {
  return (error: unknown) =>
    toast({
      title,
      description: getErrorMessage(error),
      variant: "destructive",
    })
}

export function DictationButton({
  onTranscribed,
}: {
  onTranscribed: (text: string) => void
}): React.ReactElement {
  const recorder = useWhisperTranscription()
  const transcription = useTranscriptionMutation()

  const transcribe = (file: File) =>
    transcription.mutate(
      { bodyTraceTranscribe: { file } },
      {
        onSuccess: ({ text }) => onTranscribed(text),
        onError: showFailure("Dictation failed"),
      },
    )

  switch (recorder.status) {
    case "recording":
    case "paused":
      return (
        <div className="flex items-center gap-1 rounded-md bg-background">
          <LinearAudioLevels levels={recorder.levels} barCount={16} />
          <StartStop
            icon={X}
            aria-label="Cancel dictation"
            onMouseDown={keepFocus}
            onClick={() =>
              recorder.stop().catch(showFailure("Dictation failed"))
            }
          />
          <StartStop
            icon={Check}
            aria-label="Insert dictation"
            onMouseDown={keepFocus}
            onClick={() =>
              recorder
                .accept()
                .then(transcribe, showFailure("Dictation failed"))
            }
          />
        </div>
      )
    case "idle":
    case "complete":
      return transcription.isPending ? (
        <StartStop icon={LoadingSpinner} disabled />
      ) : (
        <StartStop
          icon={Mic}
          aria-label="Dictate"
          onMouseDown={keepFocus}
          onClick={() =>
            recorder.start().catch(showFailure("Microphone unavailable"))
          }
        />
      )
  }
}
